// src/services/hotelApi.js
// Broadway Holidays — Booking.com API via booking-com15.p.rapidapi.com
// ─────────────────────────────────────────────────────────────

const KEY  = process.env.REACT_APP_RAPIDAPI_KEY;
const HOST = process.env.REACT_APP_RAPIDAPI_HOST;
const H    = { "X-RapidAPI-Key": KEY, "X-RapidAPI-Host": HOST };

// ── Cache ─────────────────────────────────────────────────────
const CACHE    = {};
const INFLIGHT = {};
export const clearCache = () => { Object.keys(CACHE).forEach(k => delete CACHE[k]); };

// ── Core fetch ────────────────────────────────────────────────
async function _fetch(url, { bypassCache = false, retries = 2, delay = 1500 } = {}) {
  if (!bypassCache && CACHE[url]) return CACHE[url];
  /* Also skip INFLIGHT when bypassCache — ensures fresh call when filters change */
  if (!bypassCache && INFLIGHT[url]) return INFLIGHT[url];
  const run = async () => {
    const r = await fetch(url, { headers: H });
    if (r.status === 429) {
      if (retries > 0) { await sleep(delay); return _fetch(url, { bypassCache:true, retries:retries-1, delay:delay*2 }); }
      throw new Error("Rate limited — please wait a moment.");
    }
    if (!r.ok) throw new Error(`API ${r.status}: ${r.statusText}`);
    const j = await r.json();
    if (!bypassCache) CACHE[url] = j;
    return j;
  };
  INFLIGHT[url] = run().finally(() => delete INFLIGHT[url]);
  return INFLIGHT[url];
}
const sleep = ms => new Promise(r => setTimeout(r, ms));
const api   = path => `https://${HOST}${path}`;

// ── Helpers ───────────────────────────────────────────────────
export const _s = v => {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) return _s(v[0]);
  if (typeof v === "object") return _s(v.name||v.value||v.label||v.text||v.title||v.description||"");
  return "";
};
export const fixPhoto = url => {
  if (!url || typeof url !== "string" || !url.startsWith("http")) return null;
  return url
    .replace(/\/square60\//,  "/max500/")
    .replace(/\/square600\//, "/max500/")
    .replace(/\/square300\//, "/max500/")
    .replace(/\/small\//,     "/max500/")
    .replace(/\/max1280\//,   "/max500/");
};
export const MARKUP      = 1.05;
export const applyMarkup = p => { const n=Number(p); return (!n||n<=0)?null:Math.ceil(n*MARKUP); };
export const markup      = applyMarkup;
export const MEAL_IDS    = { "All Inclusive":9,"Half Board":3,"Full Board":4,"Bed & Breakfast":1,"Room Only":0 };
export const MEAL_NAMES  = Object.fromEntries(Object.entries(MEAL_IDS).map(([k,v])=>[v,k]));

// ── City → IATA ───────────────────────────────────────────────
export const DEST_IATA = {
  "tenerife":"TFS","lanzarote":"ACE","fuerteventura":"FUE","gran canaria":"LPA",
  "majorca":"PMI","mallorca":"PMI","menorca":"MAH","ibiza":"IBZ",
  "alicante":"ALC","malaga":"AGP","marbella":"AGP","barcelona":"BCN","madrid":"MAD",
  "antalya":"AYT","bodrum":"BJV","dalaman":"DLM","marmaris":"DLM","istanbul":"SAW",
  "dubai":"DXB","abu dhabi":"AUH","maldives":"MLE",
  "thailand":"BKK","bangkok":"BKK","phuket":"HKT","bali":"DPS",
  "egypt":"HRG","hurghada":"HRG","sharm":"SSH",
  "portugal":"FAO","lisbon":"LIS","algarve":"FAO","faro":"FAO","porto":"OPO",
  "rhodes":"RHO","kos":"KGS","crete":"HER","corfu":"CFU","santorini":"JTR","zante":"ZTH",
  "athens":"ATH","greece":"ATH","malta":"MLA","cyprus":"PFO","paphos":"PFO",
  "amsterdam":"AMS","paris":"CDG","rome":"FCO","milan":"MXP","berlin":"BER",
  "new york":"JFK","florida":"MCO","orlando":"MCO","miami":"MIA",
  "mexico":"CUN","cancun":"CUN","dominican":"PUJ","punta cana":"PUJ",
  "barbados":"BGI","jamaica":"KIN","morocco":"RAK","marrakech":"RAK","tunisia":"TUN",
};
export const cityToIATA = city => {
  if (!city) return null;
  const c = city.toLowerCase();
  for (const [k,v] of Object.entries(DEST_IATA)) if (c.includes(k)) return v;
  return null;
};
export const toFlightId = code => code ? `${code}.AIRPORT` : null;

// ── 1. Destination search ─────────────────────────────────────
// Customers can type ANY place: a city (Agadir), a region / zone
// (Costa Brava, Costa del Sol, Algarve), a country (Egypt) or an area
// (Playa de las Americas). Typos like "costa barva" are corrected.

// Popular names used for typo-correction + instant suggestions.
export const KNOWN_DESTS = [
  // Countries
  "Spain","Portugal","Greece","Turkey","Egypt","Morocco","Cyprus","Malta","Italy","France",
  "Croatia","Tunisia","Bulgaria","Montenegro","Albania","Maldives","Mauritius","Seychelles",
  "Sri Lanka","Thailand","Indonesia","Mexico","Dominican Republic","Jamaica","Barbados",
  "Cuba","United Arab Emirates","Qatar","Oman","Jordan","Cape Verde","Gambia","Kenya",
  // Spanish zones / regions / islands
  "Costa Brava","Costa del Sol","Costa Blanca","Costa Dorada","Costa Calida","Costa de la Luz",
  "Costa Almeria","Canary Islands","Balearic Islands","Tenerife","Lanzarote","Gran Canaria",
  "Fuerteventura","La Palma","La Gomera","Majorca","Menorca","Ibiza","Formentera",
  "Benidorm","Salou","Lloret de Mar","Tossa de Mar","Malaga","Marbella","Torremolinos",
  "Fuengirola","Benalmadena","Alicante","Barcelona","Madrid","Seville","Valencia",
  "Playa de las Americas","Los Cristianos","Costa Adeje","Puerto de la Cruz","Playa del Ingles",
  "Maspalomas","Puerto Rico Gran Canaria","Corralejo","Caleta de Fuste","Costa Teguise",
  "Puerto del Carmen","Playa Blanca","Alcudia","Magaluf","Palma Nova","Cala d'Or","San Antonio",
  // Portugal
  "Algarve","Albufeira","Vilamoura","Lagos","Faro","Portimao","Lisbon","Porto","Madeira","Funchal","Azores",
  // Greece
  "Crete","Corfu","Rhodes","Zante","Kos","Kefalonia","Mykonos","Santorini","Skiathos","Halkidiki",
  "Athens","Thassos","Lefkada","Paxos","Chania","Heraklion","Hersonissos","Malia","Lindos","Faliraki",
  // Turkey
  "Antalya","Belek","Side","Alanya","Kemer","Lara","Bodrum","Marmaris","Dalaman","Fethiye",
  "Oludeniz","Icmeler","Kusadasi","Istanbul","Turkish Riviera","Dalyan","Kalkan",
  // Egypt / Morocco / Tunisia
  "Hurghada","Sharm El Sheikh","Makadi Bay","Sahl Hasheesh","El Gouna","Marsa Alam","Soma Bay",
  "Luxor","Cairo","Agadir","Marrakech","Taghazout","Essaouira","Fez","Tangier","Hammamet",
  "Sousse","Monastir","Djerba",
  // Cyprus / Malta / Italy / France / Croatia / Bulgaria
  "Paphos","Ayia Napa","Protaras","Limassol","Larnaca","Sliema","St Julian's","Gozo",
  "Rome","Venice","Florence","Sorrento","Amalfi Coast","Lake Garda","Sicily","Sardinia",
  "Paris","Nice","French Riviera","Dubrovnik","Split","Sunny Beach","Golden Sands","Budva",
  // Long haul
  "Dubai","Abu Dhabi","Ras Al Khaimah","Doha","Muscat","Phuket","Krabi","Koh Samui","Bangkok",
  "Bali","Cancun","Riviera Maya","Playa del Carmen","Tulum","Punta Cana","Montego Bay",
  "Negril","Varadero","Sal","Boa Vista","Goa","Male","Mombasa","Orlando","New York","Las Vegas",
];

// Common alternative spellings → canonical name
const ALIASES = {
  "mallorca":"Majorca","majorca":"Majorca","minorca":"Menorca","zakynthos":"Zante",
  "sharm":"Sharm El Sheikh","sharm el shiekh":"Sharm El Sheikh","sharm al sheikh":"Sharm El Sheikh",
  "uae":"United Arab Emirates","marrakesh":"Marrakech","kerkyra":"Corfu","kerkira":"Corfu",
  "rodos":"Rhodes","kriti":"Crete","cephalonia":"Kefalonia","chalkidiki":"Halkidiki",
  "tenerife south":"Costa Adeje","playa las americas":"Playa de las Americas",
  "costa daurada":"Costa Dorada","algarve coast":"Algarve","egipt":"Egypt","egpyt":"Egypt",
  "morroco":"Morocco","marocco":"Morocco","turkiye":"Turkey","grece":"Greece",
};

const norm = s => String(s||"")
  .toLowerCase()
  .normalize("NFD").replace(/[̀-ͯ]/g, "")   // strip accents
  .replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();

function lev(a, b) {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

// "costa barva" → "Costa Brava", "agadirr" → "Agadir". Returns null if no confident match.
export function correctDestination(query) {
  const q = norm(query);
  if (!q) return null;
  if (ALIASES[q]) return ALIASES[q];
  let best = null, bestD = Infinity;
  for (const name of KNOWN_DESTS) {
    const n = norm(name);
    if (n === q) return name;
    const d = lev(q, n);
    if (d < bestD) { bestD = d; best = name; }
  }
  const allowed = q.length <= 4 ? 1 : q.length <= 8 ? 2 : 3;
  return bestD <= allowed ? best : null;
}

// Local instant suggestions (exact/prefix/contains first, then typo matches)
export function localSuggestions(query, limit = 8) {
  const q = norm(query);
  if (!q) return [];
  const scored = [];
  for (const name of KNOWN_DESTS) {
    const n = norm(name);
    let score = null;
    if (n === q) score = 0;
    else if (n.startsWith(q)) score = 1;
    else if (n.split(" ").some(w => w.startsWith(q))) score = 2;
    else if (n.includes(q)) score = 3;
    if (score !== null) scored.push([score, name]);
  }
  scored.sort((a, b) => a[0] - b[0] || a[1].localeCompare(b[1]));
  const out = scored.map(x => x[1]);
  if (out.length < limit) {
    const fix = correctDestination(query);
    if (fix && !out.includes(fix)) out.push(fix);
  }
  return out.slice(0, limit);
}

// Destination types that are real "places" to search hotels in
const PLACE_TYPES = ["city", "region", "country", "district"];
const TYPE_RANK   = { city: 0, region: 1, district: 2, country: 3 };

function placeName(x) { return _s(x?.name || x?.city_name || x?.label || ""); }

// How well an API result matches what the customer typed (lower = better)
function matchScore(item, query) {
  const q  = norm(query);
  const nm = norm(placeName(item));
  const lb = norm(item?.label);
  const t  = String(item?.dest_type || item?.search_type || "").toLowerCase();
  let s;
  if (nm === q)                       s = 0;
  else if (nm.startsWith(q) || q.startsWith(nm)) s = 10;
  else if (lb.includes(q))            s = 20;
  else                                s = 30 + Math.min(lev(q, nm), 20);
  if (!PLACE_TYPES.includes(t)) s += 100;   // hotels / landmarks / airports last
  else s += TYPE_RANK[t] ?? 5;
  // more hotels = more likely the place they mean
  s -= Math.min(Number(item?.nr_hotels || item?.hotels || 0), 5000) / 5000;
  return s;
}

const DEST_CACHE = {};
export async function searchDestination(query) {
  const key = norm(query);
  if (!key) return [];
  if (DEST_CACHE[key]) return DEST_CACHE[key];
  const d = await _fetch(api(`/api/v1/hotels/searchDestination?query=${encodeURIComponent(query.trim())}`));
  const list = Array.isArray(d?.data) ? d.data : [];
  DEST_CACHE[key] = list;
  return list;
}

function pickBest(list, query) {
  if (!list.length) return null;
  const ranked = [...list].sort((a, b) => matchScore(a, query) - matchScore(b, query));
  return ranked[0];
}
const isGoodMatch = (item, query) => item && matchScore(item, query) < 60;

// Live suggestions for the search box (API results, places only, best first)
export async function suggestDestinations(query, limit = 8) {
  const q = (query || "").trim();
  if (q.length < 2) return [];
  let list = await searchDestination(q).catch(() => []);
  let usedQuery = q;
  if (!list.some(x => isGoodMatch(x, q))) {
    const fix = correctDestination(q);
    if (fix && norm(fix) !== norm(q)) {
      const alt = await searchDestination(fix).catch(() => []);
      if (alt.length) { list = alt; usedQuery = fix; }
    }
  }
  const seen = new Set();
  return list
    .filter(x => PLACE_TYPES.includes(String(x?.dest_type || x?.search_type || "").toLowerCase()))
    .sort((a, b) => matchScore(a, usedQuery) - matchScore(b, usedQuery))
    .filter(x => { const k = `${x.dest_id}|${x.search_type}`; if (seen.has(k)) return false; seen.add(k); return true; })
    .slice(0, limit)
    .map(x => ({
      name:        placeName(x),
      label:       _s(x.label) || placeName(x),
      dest_id:     x.dest_id,
      search_type: x.search_type || x.dest_type,
      type:        String(x.dest_type || x.search_type || "").toLowerCase(),
      hotels:      Number(x.nr_hotels || x.hotels || 0),
      corrected:   usedQuery !== q ? usedQuery : null,
    }));
}

export const getDestination = async (query) => {
  const q = (query || "").trim();
  if (!q) return null;
  let list = await searchDestination(q).catch(() => []);
  let best = pickBest(list, q);
  // Nothing sensible (e.g. a typo like "costa barva") → retry with the corrected name
  if (!isGoodMatch(best, q)) {
    const fix = correctDestination(q);
    if (fix && norm(fix) !== norm(q)) {
      const alt = await searchDestination(fix).catch(() => []);
      const altBest = pickBest(alt, fix);
      if (altBest) best = altBest;
    }
  }
  return best ? { dest_id: best.dest_id, search_type: best.search_type || best.dest_type, label: best.label, name: placeName(best) } : null;
};

// ── 2. Search Hotels ──────────────────────────────────────────
// booking-com15 RapidAPI CORRECT filter param names:
//   categories_filter : "mealplan-9,mealplan-3,facility-11"  (meals + facilities)
//   class_interval    : "4,5"   (star ratings comma-separated)
//   review_score_filter: "80"   (minimum score × 10, e.g. 80 = 8.0+)
//   price_filter_currencycode + price_filter_min + price_filter_max
//   sort_by           : "popularity" | "class_ascending" | "class_descending" | "distance" | "upsort_bh" | "review_score" | "price"
export async function searchHotels(city, checkIn, checkOut, adults=2, rooms=1, filters={}) {
  // If the customer picked a suggestion we already know the exact Booking.com id
  const dest = (filters.dest_id && filters.search_type)
    ? { dest_id: filters.dest_id, search_type: filters.search_type }
    : await getDestination(city);
  if (!dest?.dest_id) return [];

  const p = new URLSearchParams({
    dest_id:       dest.dest_id,
    search_type:   dest.search_type,
    arrival_date:  checkIn,
    departure_date:checkOut,
    adults:        String(adults),
    room_qty:      String(rooms),
    page_number:   "1",
    units:         "metric",
    languagecode:  "en-us",
    currency_code: "GBP",
    sort_by:       filters.sort_by || "popularity",
  });

  // ── Meal types → try ALL known param formats for booking-com15 ──
  // meal_types[] is the most reliable; categories_filter as backup
  if (Array.isArray(filters.meal_types) && filters.meal_types.length > 0) {
    // Primary: meal_types[] array params
    filters.meal_types.forEach(id => p.append("meal_types[]", String(id)));
    // Also send as categories_filter with dash format
    const catStr = filters.meal_types.map(id => `mealplan-${id}`).join(",");
    p.set("categories_filter", catStr);
  } else if (filters.categories_filter) {
    // Direct categories_filter string passed
    p.set("categories_filter", filters.categories_filter);
  }

  // ── Facility filter ────────────────────────────────────────────
  if (Array.isArray(filters.facility_ids) && filters.facility_ids.length > 0) {
    filters.facility_ids.forEach(id => p.append("room_facility_id[]", String(id)));
  }

  // ── Star rating ────────────────────────────────────────────────
  if (Array.isArray(filters.stars) && filters.stars.length > 0) {
    filters.stars.forEach(s => p.append("class[]", String(s)));
    p.set("class_interval", filters.stars.map(String).join(","));
  }

  // ── Review score ───────────────────────────────────────────────
  if (filters.review_score && Number(filters.review_score) > 0) {
    p.set("review_score_filter", String(filters.review_score));
    p.set("review_score", String(filters.review_score));
  }

  // ── Price ──────────────────────────────────────────────────────
  if (filters.price_max && Number(filters.price_max) > 0) {
    p.set("price_filter_currencycode", "GBP");
    p.set("price_filter_max", String(filters.price_max));
  }

  const url = api(`/api/v1/hotels/searchHotels?${p}`);

  const hasFilters =
    (filters.meal_types?.length > 0) ||
    !!(filters.categories_filter) ||
    (filters.facility_ids?.length > 0) ||
    (filters.stars?.length > 0) ||
    filters.review_score ||
    filters.price_max ||
    filters._bypassCache;

  if (hasFilters) {
    delete CACHE[url];
    console.log("[Broadway] Meal filter URL params:", 
      [...p.entries()].filter(([k])=>["meal_types[]","categories_filter","class[]","class_interval","review_score_filter"].includes(k)).map(([k,v])=>`${k}=${v}`).join(" | ") || "none"
    );
  }

  const d    = await _fetch(url, { bypassCache: !!hasFilters });
  const list = d?.data?.result || d?.data?.hotels || d?.data?.properties || [];
  return Array.isArray(list) ? list : [];
}

// ── 3. Get Filter options ─────────────────────────────────────
export async function getApiFilters(dest_id, search_type, checkIn, checkOut, adults=2, rooms=1) {
  try {
    const url = api(`/api/v1/hotels/getFilter?dest_id=${dest_id}&search_type=${search_type}&arrival_date=${checkIn}&departure_date=${checkOut}&adults=${adults}&room_qty=${rooms}&currency_code=GBP`);
    const d = await _fetch(url);
    const raw = d?.data?.filters || d?.data?.filter_options || d?.data || [];
    const result = {};
    const KEY_MAP = {
      mealplan:"meal_types", meal_type:"meal_types", board:"meal_types", boardbasis:"meal_types",
      class:"stars", star_rating:"stars", hotel_class:"stars",
      review_score:"review_score", guestrating:"review_score",
      district:"resort", neighborhood:"resort", area:"resort",
      propertytype:"property_type",
    };
    (Array.isArray(raw)?raw:[]).forEach(f => {
      const rawId = (f?.filter_id||f?.id||f?.type||"").toLowerCase();
      const key   = KEY_MAP[rawId] || rawId;
      if (key) result[key] = {
        name:    _s(f?.name||rawId),
        raw_id:  rawId,
        options: (f?.options||f?.items||[]).map(o=>({
          id:    o?.id ?? o?.value ?? o?.filter_id,
          name:  _s(o?.name||o?.title||o?.label||String(o?.id||"")),
          count: Number(o?.count||o?.nb_hotels||0),
        })).filter(o=>o.name),
      };
    });
    return result;
  } catch { return {}; }
}

// ── 4. Hotel Details ──────────────────────────────────────────
export async function getHotelDetails(hotelId) {
  try { const d=await _fetch(api(`/api/v1/hotels/getHotelDetails?hotel_id=${hotelId}`)); return d?.data||{}; }
  catch { return {}; }
}

// ── 5. Hotel Photos ───────────────────────────────────────────
export async function getHotelPhotos(hotelId) {
  try {
    const d = await _fetch(api(`/api/v1/hotels/getHotelPhotos?hotel_id=${hotelId}`));
    let arr = [];
    if (Array.isArray(d?.data)) arr=d.data;
    else if (Array.isArray(d?.data?.photos)) arr=d.data.photos;
    else if (Array.isArray(d)) arr=d;
    else if (Array.isArray(d?.photos)) arr=d.photos;
    const urls = arr.map(p=>{
      if (typeof p==="string") return fixPhoto(p);
      if (typeof p==="object") return fixPhoto(p.url_original||p.url_max1280||p.url_square60||p.url_max300||p.url||p.photo);
      return null;
    }).filter(u=>u&&u.startsWith("http"));
    return [...new Set(urls)].slice(0,50);
  } catch { return []; }
}

// ── 6. Description ────────────────────────────────────────────
export async function getDescriptionAndInfo(hotelId) {
  try {
    const d   = await _fetch(api(`/api/v1/hotels/getDescriptionAndInfo?hotel_id=${hotelId}`));
    const arr = Array.isArray(d?.data)?d.data:Array.isArray(d?.result)?d.result:[];
    if (!arr.length) return _s(d?.data?.description||d?.description||"");
    const main = arr.find(b=>b?.languagecode?.startsWith("en")&&String(b?.description||"").length>30)||arr[0];
    return _s(main?.description||main?.text||main?.content||main);
  } catch { return ""; }
}

// ── 7. Facilities ─────────────────────────────────────────────
export async function getHotelFacilities(hotelId) {
  try {
    const d      = await _fetch(api(`/api/v1/hotels/getHotelFacilities?hotel_id=${hotelId}`));
    const groups = d?.data||d?.result||[];
    const all    = [];
    (Array.isArray(groups)?groups:[]).forEach(g=>{
      const type  = _s(g?.facilitytype_name||g?.type_name||g?.name||"Other");
      const items = g?.facilities||g?.items||[];
      (Array.isArray(items)?items:[]).forEach(f=>{
        const name = _s(f?.name||f?.facility_name||f?.title||f);
        if (name&&name.length<60) all.push({type,name});
      });
    });
    return all;
  } catch { return []; }
}

// ── 8. Rooms with Availability — tries ALL endpoints ─────────────
// booking-com15 has multiple room endpoints, we try them all
export async function getRoomsWithAvailability(hotelId, checkIn, checkOut, adults=2, rooms=1) {
  
  // Helper: extract room array from ANY response shape
  const extractRooms = (d) => {
    if (!d) return [];
    // Shape 1: d.data.rooms (most common)
    if (Array.isArray(d?.data?.rooms) && d.data.rooms.length > 0) return d.data.rooms;
    // Shape 2: d.data.result
    if (Array.isArray(d?.data?.result) && d.data.result.length > 0) return d.data.result;
    // Shape 3: d.data is array directly
    if (Array.isArray(d?.data) && d.data.length > 0) return d.data;
    // Shape 4: d.result
    if (Array.isArray(d?.result) && d.result.length > 0) return d.result;
    // Shape 5: d.rooms
    if (Array.isArray(d?.rooms) && d.rooms.length > 0) return d.rooms;
    // Shape 6: d itself is array
    if (Array.isArray(d) && d.length > 0) return d;
    return [];
  };

  // booking-com15 room endpoints use DIFFERENT param names than searchHotels
  // Try every known variation to maximise chance of getting data
  const base = `hotel_id=${hotelId}&currency_code=GBP&languagecode=en-us&units=metric`;
  const dates1 = `arrival_date=${checkIn}&departure_date=${checkOut}`;
  const dates2 = `checkin_date=${checkIn}&checkout_date=${checkOut}`;
  const dates3 = `check_in=${checkIn}&check_out=${checkOut}`;
  const pax1   = `adults=${adults}&room_qty=${rooms}`;
  const pax2   = `adults_number=${adults}&room_number=${rooms}`;
  const children = `children_age=0`;

  // Try endpoints in order — most likely to return data first
  const endpoints = [
    // getRoomList with standard params
    `/api/v1/hotels/getRoomList?${base}&${dates1}&${pax1}`,
    // getRoomList with alternate param names
    `/api/v1/hotels/getRoomList?${base}&${dates1}&${pax2}&${children}`,
    // getRoomListWithAvailability - standard
    `/api/v1/hotels/getRoomListWithAvailability?${base}&${dates1}&${pax1}`,
    // getRoomListWithAvailability - alternate params
    `/api/v1/hotels/getRoomListWithAvailability?${base}&${dates2}&${pax2}`,
    // getRoomList with alternate date format
    `/api/v1/hotels/getRoomList?${base}&${dates2}&${pax1}`,
    // getAvailability - different endpoint entirely  
    `/api/v1/hotels/getAvailability?${base}&${dates1}&${pax1}`,
    `/api/v1/hotels/getAvailability?hotel_id=${hotelId}&${dates3}&adults=${adults}&rooms=${rooms}&currency_code=GBP`,
  ];

  for (const path of endpoints) {
    try {
      const d = await _fetch(api(path));
      const list = extractRooms(d);
      
      if (list.length > 0) {
        // Log first room so we can see what fields are available
        console.log(`[hotelApi] ${path.split('?')[0].split('/').pop()} returned ${list.length} rooms. First room fields:`, Object.keys(list[0]).join(', '));
        console.log(`[hotelApi] First room meal fields:`, {
          mealplan: list[0]?.mealplan,
          meal_plan: list[0]?.meal_plan,
          meal_type_id: list[0]?.meal_type_id,
          board: list[0]?.board,
          mealPlan: list[0]?.mealPlan,
          block_mealplan: list[0]?.block?.[0]?.mealplan,
          price: list[0]?.price,
          min_price: list[0]?.min_price,
          product_price: list[0]?.product_price_breakdown?.gross_amount?.value,
        });
        return { rooms: list, source: path.includes('Availability') ? 'availability' : 'roomlist' };
      }
    } catch (e) {
      console.warn(`[hotelApi] ${path.split('?')[0].split('/').pop()} failed:`, e.message);
    }
  }

  console.warn('[hotelApi] All room endpoints returned empty. Hotel may have no availability for these dates.');
  return { rooms: [], source: 'none' };
}
export const getRoomList = (id,ci,co,a,r) => getRoomsWithAvailability(id,ci,co,a,r).then(x=>x.rooms);
export const getRoomData = getRoomList;

export function extractRoomBoard(room) {
  // Check EVERY possible location booking-com15 API puts meal plan data
  // Different API versions use different field names!
  const raw =
    room?.mealplan ||
    room?.meal_plan ||
    room?.board ||
    room?.board_type ||
    room?.mealPlan ||
    room?.boardBasis ||
    room?.board_basis ||
    room?.meal_included ||
    room?.block?.[0]?.mealplan ||
    room?.block?.[0]?.meal_plan ||
    room?.block?.[0]?.board ||
    room?.block?.[0]?.mealIncluded ||
    room?.rateplans?.[0]?.mealplan ||
    room?.rateplans?.[0]?.meal_plan ||
    room?.rateplans?.[0]?.board ||
    room?.room_data_trans?.mealplan ||
    room?.room_data_trans?.meal_plan ||
    room?.paymentterms?.[0]?.mealplan ||
    "";
  const m = _s(raw).toLowerCase();
  if (m.includes("all inclus"))    return "All Inclusive";
  if (m.includes("half board") || m === "hb") return "Half Board";
  if (m.includes("full board") || m === "fb") return "Full Board";
  if (m.includes("breakfast") || m.includes("b&b")) return "Bed & Breakfast";
  if (m.includes("self cater") || m.includes("kitchen")) return "Self Catering";
  if (m.includes("room only") || m.includes("no meal") || m === "ro") return "Room Only";
  // Check meal_type_id numeric mapping
  const mid = room?.meal_type_id ?? room?.mealTypeId ?? room?.meal_id ??
              room?.block?.[0]?.meal_type_id;
  if (mid != null) {
    const mapped = MEAL_NAMES[Number(mid)];
    if (mapped) return mapped;
  }
  return "";
}

// ── 9. Reviews ────────────────────────────────────────────────
export async function getHotelReviews(hotelId, page=1) {
  try {
    const d    = await _fetch(api(`/api/v1/hotels/getHotelReviews?hotel_id=${hotelId}&languagecode=en-us&page_number=${page}&sort_by=SORT_MOST_RELEVANT`));
    const raw  = d?.data?.result||d?.data?.reviews||[];
    const items = Array.isArray(raw)?raw:[];
    const sc   = d?.data?.average_score||d?.data?.review_score||0;
    return {items,total:+(d?.data?.count||items.length||0),avgScore:typeof sc==="object"?+(sc?.value||0):+sc,breakdown:d?.data?.score_breakdown||null};
  } catch { return {items:[],total:0,avgScore:0,breakdown:null}; }
}

// ── 10. Policies ──────────────────────────────────────────────
export async function getHotelPolicies(hotelId) {
  try {
    const d   = await _fetch(api(`/api/v1/hotels/getHotelPolicies?hotel_id=${hotelId}`));
    const raw = d?.data||d?.result||{};
    const parsed = {};
    const extractText = v => {
      if (typeof v==="string") return v.replace(/\\n|\n/g," ").replace(/\.t/g," ").trim();
      if (Array.isArray(v)) {
        const en = v.find(x=>x?.languagecode?.startsWith("en")||x?.language?.startsWith("en"))||v[0];
        return extractText(en?.text||en?.description||en?.content||en||"");
      }
      if (typeof v==="object"&&v!==null) {
        if (v?.content) return extractText(v.content);
        if (v?.text)    return extractText(v.text);
        if (Array.isArray(v?.policy)) return v.policy.map(p=>extractText(p?.content||p?.text||"")).filter(Boolean).join(" ");
        return "";
      }
      return String(v||"").replace(/\\n|\n/g," ").trim();
    };
    const arr  = Array.isArray(raw)?raw:[raw];
    const seen = new Set();
    arr.forEach(group=>{
      const type = _s(group?.type||group?.policygroup_type||group?.name||"Policy");
      const displayName = type.replace(/POLICY_/gi,"").replace(/_/g," ").toLowerCase().replace(/^./,c=>c.toUpperCase());
      if (!displayName||seen.has(displayName)) return;
      seen.add(displayName);
      const text = extractText(group?.policy||group?.content||group?.text||group?.description||"");
      if (text&&text.length>5&&!text.startsWith("[{")) parsed[displayName]=text;
    });
    return Object.keys(parsed).length?parsed:raw;
  } catch { return {}; }
}

// ── 10b. Hotel Review Scores (better structured than getHotelReviews) ──
export async function getHotelReviewScores(hotelId) {
  try {
    const d = await _fetch(api(`/api/v1/hotels/getHotelReviewScores?hotel_id=${hotelId}`));
    const data = d?.data || d?.result || d || {};
    // Returns category scores: cleanliness, location, facilities, staff etc.
    const scores = {};
    const arr = Array.isArray(data) ? data : (Array.isArray(data?.scores) ? data.scores : []);
    arr.forEach(s => {
      const k = _s(s?.question||s?.category||s?.type||s?.name||"");
      const v = Number(s?.score||s?.value||s?.rating||0);
      if (k && v > 0) scores[k] = v;
    });
    return {
      overall: Number(data?.average_score||data?.overall_score||data?.total_score||0),
      scores,
      totalReviews: Number(data?.total_count||data?.review_count||0),
      raw: data,
    };
  } catch { return { overall: 0, scores: {}, totalReviews: 0 }; }
}

// ── 10c. Payment Features ──────────────────────────────────────────
export async function getPaymentFeatures(hotelId) {
  try {
    const d = await _fetch(api(`/api/v1/hotels/getPaymentFeatures?hotel_id=${hotelId}`));
    return d?.data || d?.result || {};
  } catch { return {}; }
}

// ═══════════════════════════════════════════════════════════════
// CAR RENTAL APIs
// ═══════════════════════════════════════════════════════════════

// Search car rental locations by destination name
export async function searchCarLocation(query) {
  try {
    const d = await _fetch(api(`/api/v1/cars/searchCarLocation?query=${encodeURIComponent(query)}`));
    return d?.data || d?.result || [];
  } catch { return []; }
}

// Search available car rentals
// pickUpId/dropOffId from searchCarLocation
export async function searchCarRentals({ pickUpId, dropOffId, pickUpDate, pickUpTime="10:00", dropOffDate, dropOffTime="10:00", adults=2, currency="GBP" }) {
  try {
    const p = new URLSearchParams({
      pick_up_id: pickUpId,
      drop_off_id: dropOffId || pickUpId,
      pick_up_date: pickUpDate,
      pick_up_time: pickUpTime,
      drop_off_date: dropOffDate,
      drop_off_time: dropOffTime,
      adults: String(adults),
      currency_code: currency,
      languagecode: "en-us",
      units: "metric",
    });
    const d = await _fetch(api(`/api/v1/cars/searchCarRentals?${p}`));
    const list = d?.data?.search_results || d?.data?.vehicles || d?.data?.result || d?.data || [];
    return Array.isArray(list) ? list.slice(0, 12) : [];
  } catch (e) { console.warn("searchCarRentals:", e.message); return []; }
}

// Get car rental packages for a specific vehicle
export async function getCarPackages({ vehicleId, pickUpDate, dropOffDate, currency="GBP" }) {
  try {
    const p = new URLSearchParams({ vehicle_id: vehicleId, pick_up_date: pickUpDate, drop_off_date: dropOffDate, currency_code: currency });
    const d = await _fetch(api(`/api/v1/cars/getPackages?${p}`));
    return d?.data || d?.result || {};
  } catch { return {}; }
}

// ═══════════════════════════════════════════════════════════════
// TAXI / TRANSFER APIs
// ═══════════════════════════════════════════════════════════════

// Search taxi pickup locations
export async function searchTaxiLocation(query) {
  try {
    const d = await _fetch(api(`/api/v1/taxi/searchLocation?query=${encodeURIComponent(query)}`));
    return d?.data || d?.result || [];
  } catch { return []; }
}

// Search available taxis/transfers between two points
export async function searchTaxis({ pickUpId, dropOffId, pickUpDate, pickUpTime="10:00", adults=2, currency="GBP" }) {
  try {
    const p = new URLSearchParams({
      pick_up_id: pickUpId,
      drop_off_id: dropOffId,
      pick_up_date: pickUpDate,
      pick_up_time: pickUpTime,
      adults: String(adults),
      currency_code: currency,
    });
    const d = await _fetch(api(`/api/v1/taxi/searchTaxi?${p}`));
    const list = d?.data?.result || d?.data?.vehicles || d?.data || [];
    return Array.isArray(list) ? list.slice(0, 8) : [];
  } catch (e) { console.warn("searchTaxis:", e.message); return []; }
}

// ═══════════════════════════════════════════════════════════════
// ATTRACTIONS APIs
// ═══════════════════════════════════════════════════════════════

// Search attraction locations
export async function searchAttractionLocation(query) {
  try {
    const d = await _fetch(api(`/api/v1/attraction/searchLocation?query=${encodeURIComponent(query)}`));
    return d?.data || d?.result || [];
  } catch { return []; }
}

// Search attractions in a destination
export async function searchAttractions({ id, startDate, endDate, adults=2, currency="GBP", sortBy="trending" }) {
  try {
    const p = new URLSearchParams({
      id,
      startDate: startDate || "",
      endDate: endDate || "",
      adults: String(adults),
      currency_code: currency,
      sortBy,
      languagecode: "en-us",
    });
    const d = await _fetch(api(`/api/v1/attraction/searchAttractions?${p}`));
    const list = d?.data?.products || d?.data?.attractions || d?.data?.result || d?.data || [];
    return Array.isArray(list) ? list.slice(0, 12) : [];
  } catch (e) { console.warn("searchAttractions:", e.message); return []; }
}

// Get attraction availability calendar
export async function getAttractionAvailability({ id, startDate, endDate }) {
  try {
    const p = new URLSearchParams({ id, startDate, endDate });
    const d = await _fetch(api(`/api/v1/attraction/getAvailabilityCalendar?${p}`));
    return d?.data || d?.result || {};
  } catch { return {}; }
}

// Get attraction details
export async function getAttractionDetails(id) {
  try {
    const d = await _fetch(api(`/api/v1/attraction/getAttractionDetails?id=${id}`));
    return d?.data || d?.result || {};
  } catch { return {}; }
}

// Get nearby popular attractions for a hotel
export async function getNearbyAttractions(lat, lng, currency="GBP") {
  try {
    // First find the location id from coords
    const dest = await searchAttractionLocation(`${lat},${lng}`);
    const locId = dest?.[0]?.id || dest?.[0]?.dest_id;
    if (!locId) return [];
    return await searchAttractions({ id: locId, currency, sortBy: "trending" });
  } catch { return []; }
}


export async function getFlightMinPrice({fromId,toId,departDate,returnDate,adults=2,currency="GBP"}) {
  try {
    const p = new URLSearchParams({fromId,toId,departDate,adults:String(adults),currency});
    if (returnDate) p.set("returnDate",returnDate);
    const d    = await _fetch(api(`/api/v1/flights/getMinPrice?${p}`));
    const data = d?.data;
    if (!data) return null;
    if (typeof data.price==="number") return data.price;
    if (typeof data.price==="object") return data.price?.amount||data.price?.value||null;
    if (data.cheapestPrice) return data.cheapestPrice?.amount||data.cheapestPrice?.value||null;
    if (Array.isArray(data.days)&&data.days.length) {
      const prices = data.days.map(x=>{const p=x?.price;return typeof p==="number"?p:(p?.amount||p?.value||0);}).filter(p=>p>0);
      return prices.length?Math.min(...prices):null;
    }
    return null;
  } catch { return null; }
}

// ── 12. Price calendar ────────────────────────────────────────
export async function getMinPriceForMonth({fromId,toId,year,month,nights=7,adults=2,currency="GBP"}) {
  if (!fromId||!toId) return {};
  const prices={},daysInMo=new Date(year,month,0).getDate();
  const sampleDays=[1,4,8,12,15,19,22,26].filter(d=>d<=daysInMo),realPrices={};
  await Promise.allSettled(sampleDays.map(async day=>{
    const dep=`${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    const ret=new Date(year,month-1,day+nights).toISOString().split("T")[0];
    try {
      const url=api(`/api/v1/flights/getMinPrice?fromId=${fromId}&toId=${toId}&departDate=${dep}&returnDate=${ret}&adults=${adults}&currency=${currency}`);
      delete CACHE[url];
      const d=await _fetch(url,{bypassCache:true});
      const data=d?.data;
      let p=null;
      if (typeof data?.price==="number") p=data.price;
      else if (typeof data?.price==="object") p=data.price?.amount||data.price?.value;
      else if (data?.cheapestPrice) p=data.cheapestPrice?.amount||data.cheapestPrice?.value;
      if (p&&p>0) realPrices[day]=Math.ceil(p*MARKUP);
    } catch {}
  }));
  if (!Object.keys(realPrices).length) return {};
  const vals=Object.values(realPrices),avg=vals.reduce((a,b)=>a+b,0)/vals.length;
  for (let day=1;day<=daysInMo;day++) {
    const iso=`${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    const wknd=new Date(year,month-1,day).getDay(),bump=(wknd===5||wknd===6)?Math.round(avg*0.07):0;
    if (realPrices[day]) prices[iso]=realPrices[day]+bump;
    else {
      const before=sampleDays.filter(d=>d<day&&realPrices[d]),after=sampleDays.filter(d=>d>day&&realPrices[d]);
      if (before.length&&after.length) {
        const d1=before[before.length-1],d2=after[0],t=(day-d1)/(d2-d1);
        prices[iso]=Math.round(realPrices[d1]+(realPrices[d2]-realPrices[d1])*t)+bump;
      } else if (before.length) prices[iso]=realPrices[before[before.length-1]]+bump+Math.round(Math.sin((day-before[before.length-1])*0.3)*avg*0.05);
      else if (after.length) prices[iso]=realPrices[after[0]]+bump;
      else prices[iso]=Math.round(avg)+bump;
      if (prices[iso]<50) prices[iso]=50;
    }
  }
  return prices;
}

// ── 13. Search Flights ────────────────────────────────────────
export async function searchFlights({fromId,toId,departDate,returnDate,adults=2,currency="GBP"}) {
  try {
    const fId=fromId?.includes(".")?fromId:`${fromId}.AIRPORT`;
    const tId=toId?.includes(".")?toId:`${toId}.AIRPORT`;
    const p=new URLSearchParams({fromId:fId,toId:tId,departDate,adults:String(adults),currency});
    if (returnDate) p.set("returnDate",returnDate);
    const url=api(`/api/v1/flights/searchFlights?${p}`);
    delete CACHE[url];
    const d=await _fetch(url,{bypassCache:true});
    const data=d?.data;
    if (!data) return null;
    const extract=data=>{
      if (Array.isArray(data?.itineraries)&&data.itineraries.length) {
        const out=[],ret=[];
        data.itineraries.forEach(it=>{const legs=it?.legs||it?.segments||[];if(legs.length>0)out.push(legs[0]);if(legs.length>1)ret.push(legs[legs.length-1]);});
        return {out,ret};
      }
      if (data?.outbound||data?.inbound) {
        const out=data?.outbound?.flights||data?.outbound?.itineraries||(Array.isArray(data?.outbound)?data.outbound:[]);
        const ret=data?.inbound?.flights||data?.inbound?.itineraries||(Array.isArray(data?.inbound)?data.inbound:[]);
        return {out:Array.isArray(out)?out:[],ret:Array.isArray(ret)?ret:[]};
      }
      if (Array.isArray(data?.flightOffers)||Array.isArray(data?.flightDeals)) {
        const offers=data?.flightOffers||data?.flightDeals||[];
        return {out:offers.map(o=>o?.segments?.[0]||o?.legs?.[0]||o).filter(Boolean),ret:offers.map(o=>o?.segments?.[1]||o?.legs?.[1]||null).filter(Boolean)};
      }
      if (Array.isArray(data)&&data.length&&(data[0]?.outbound||data[0]?.inbound)) return {out:[data[0].outbound].filter(Boolean),ret:[data[0].inbound].filter(Boolean)};
      return {out:[],ret:[]};
    };
    const {out,ret}=extract(data);
    const parse=fl=>{
      if (!fl) return null;
      const dep=fl?.departure?.time||fl?.departureTime||fl?.dep_time||fl?.departureAt||fl?.legs?.[0]?.departure?.time||fl?.segments?.[0]?.departure?.time||"";
      const arr=fl?.arrival?.time||fl?.arrivalTime||fl?.arr_time||fl?.arrivalAt||fl?.legs?.[0]?.arrival?.time||fl?.segments?.[0]?.arrival?.time||"";
      const depAp=_s(fl?.departure?.airport?.iata||fl?.departureAirport?.iata||fl?.departureAirport?.code||fl?.origin?.iata||fl?.from||fl?.legs?.[0]?.origin?.iata||"");
      const arrAp=_s(fl?.arrival?.airport?.iata||fl?.arrivalAirport?.iata||fl?.arrivalAirport?.code||fl?.destination?.iata||fl?.to||fl?.legs?.[0]?.destination?.iata||"");
      const airline=_s(fl?.airline?.name||fl?.carrierName||fl?.marketingCarrier?.name||fl?.carriers?.[0]?.name||fl?.carrier?.name||fl?.legs?.[0]?.carriers?.[0]?.name||"");
      const flNo=_s(fl?.flightNumber||fl?.flight_number||fl?.flightIdentifier||fl?.legs?.[0]?.flightNumber||"");
      const dur=_s(fl?.duration?.text||fl?.duration||fl?.totalDuration||"");
      const stops=Number(fl?.stopCount??fl?.numberOfStops??fl?.stops??0);
      return {dep,arr,depAp,arrAp,airline,flNo,dur,stops};
    };
    const result={outbound:out.slice(0,5).map(parse).filter(Boolean),inbound:ret.slice(0,5).map(parse).filter(Boolean)};
    return result.outbound.length?result:null;
  } catch(e) { console.warn("searchFlights:",e.message); return null; }
}

export async function getFlightDetails(token) {
  if (!token) return null;
  try { const d=await _fetch(api(`/api/v1/flights/getFlightDetails?token=${encodeURIComponent(token)}`)); return d?.data||d||null; }
  catch { return null; }
}

export async function getFacilitiesForFilter(hotelId) {
  try {
    const d=await _fetch(api(`/api/v1/hotels/getHotelFacilities?hotel_id=${hotelId}`));
    const groups=d?.data||d?.result||[],result={};
    (Array.isArray(groups)?groups:[]).forEach(g=>{
      const type=_s(g?.facilitytype_name||g?.type_name||g?.name||"Other"),items=g?.facilities||g?.items||[];
      if (!result[type]) result[type]=[];
      (Array.isArray(items)?items:[]).forEach(f=>{const name=_s(f?.name||f?.facility_name||f?.title||f);if(name&&name.length<60)result[type].push(name);});
    });
    return result;
  } catch { return {}; }
}
export function extractFlightToken(flightData) { return flightData?.token||flightData?.outbound?.[0]?.token||null; }

// ── 14. Extract helpers ───────────────────────────────────────
export function extractName(det) {
  if (!det) return "";
  return _s(det.hotel_name)||_s(det.name)||_s(det.name_trans)||_s(det.hotel_name_trans)||_s(det.HotelName)||_s(det.hotelName)||_s(det.title)||_s(det.property?.name)||"";
}
export function extractDescription(det) {
  if (!det) return "";
  if (Array.isArray(det.description_translations)&&det.description_translations.length) {
    const t=det.description_translations.find(x=>x?.languagecode?.startsWith("en")&&String(x?.description||"").length>30)||det.description_translations[0];
    const txt=_s(t?.description||t?.text||t);
    if (txt&&txt.length>20) return txt;
  }
  return _s(det.description||det.hotel_description||det.HotelDescription||det.small_description||"");
}
export function extractFacilities(det) {
  if (!det) return [];
  const srcs=[det?.facilities_block?.facilities,det?.facilities,det?.hotel_facilities,det?.top_ufi_benefits];
  const out=new Set();
  for (const s of srcs) {
    if (!Array.isArray(s)) continue;
    s.forEach(f=>{const n=_s(f?.name||f?.facility_name||f?.translated_name||f?.title||f);if(n&&n.length<60)out.add(n);});
    if (out.size>=8) break;
  }
  return [...out].slice(0,24);
}

// ── 15. Normalise ─────────────────────────────────────────────
export function normalise(h) {
  const p=h?.property||{};
  const raw=p?.priceBreakdown?.grossPrice?.value||p?.priceBreakdown?.allInclusiveAmount?.value||h?.composite_price_breakdown?.gross_amount?.value||h?.min_total_price||h?.price_breakdown?.gross_price||null;
  const urls=Array.isArray(p?.photoUrls)?p.photoUrls.map(fixPhoto).filter(Boolean):(h?.main_photo_url?[fixPhoto(h.main_photo_url)||h.main_photo_url]:[]);
  return {
    id:p?.hotel_id||h?.hotel_id||p?.id||h?.id,
    name:_s(p?.name)||_s(h?.hotel_name)||_s(h?.name)||"Hotel",
    city:_s(p?.wishlistName)||_s(h?.city)||_s(h?.cityName)||_s(p?.countryCode)||"",
    address:_s(h?.address||p?.address||""),
    rating:+(p?.reviewScore||h?.review_score||h?.rating||0),
    ratingWord:_s(p?.reviewScoreWord||h?.review_score_word||""),
    reviews:+(p?.reviewCount||h?.review_nr||0),
    stars:+(p?.class||h?.class||h?.stars||0),
    price:applyMarkup(raw),
    currency:"GBP",
    photo:urls[0]||null,
    photos:urls,
    lat:p?.latitude||h?.latitude||null,
    lng:p?.longitude||h?.longitude||null,
  };
}
export const extractHotelInfo        = normalise;
export const extractHotelInformation = normalise;

export function ratingLabel(s) {
  if (s>=9.2) return "Exceptional"; if (s>=8.5) return "Excellent";
  if (s>=8.0) return "Very Good";   if (s>=7.0) return "Good";
  if (s>=6.0) return "Pleasant";    return s>0?"Reviewed":"";
}
export const getRatingLabel = ratingLabel;
export const ratingBubbles  = score => Math.min(5,Math.max(0,Math.round((score/10)*5*2)/2));